import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBarcode, faKeyboard, faSpinner, faTimes, faVideo } from '@fortawesome/free-solid-svg-icons'
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser'
import { useAutoDismiss } from './useAutoDismiss'

type BarcodeScannerProps = {
  open: boolean
  title?: string
  onClose: () => void
  onScan: (barcode: string) => void
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  open,
  title = 'Scan barcode',
  onClose,
  onScan,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const controlsRef = useRef<IScannerControls | null>(null)
  const handledRef = useRef(false)
  const [manual, setManual] = useState('')
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [useManual, setUseManual] = useState(false)
  useAutoDismiss(cameraError, () => setCameraError(null))

  const stopScanner = useCallback(() => {
    try {
      controlsRef.current?.stop()
    } catch {
      /* ignore */
    }
    controlsRef.current = null
    const video = videoRef.current
    const stream = video?.srcObject as MediaStream | null
    stream?.getTracks().forEach((t) => t.stop())
    if (video) video.srcObject = null
  }, [])

  useEffect(() => {
    if (!open) {
      stopScanner()
      handledRef.current = false
      setManual('')
      setCameraError(null)
      setUseManual(false)
      setStarting(false)
      return
    }

    if (useManual) {
      stopScanner()
      return
    }

    let cancelled = false
    handledRef.current = false
    setStarting(true)
    setCameraError(null)

    const start = async () => {
      const reader = new BrowserMultiFormatReader()
      try {
        if (!videoRef.current) return
        const controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result, _error, controls) => {
            if (cancelled || handledRef.current) return
            if (result) {
              const text = String(result.getText() || '').trim()
              if (!text) return
              handledRef.current = true
              controls.stop()
              onScan(text)
            }
          },
        )
        if (cancelled) {
          controls.stop()
          return
        }
        controlsRef.current = controls
      } catch (err: unknown) {
        if (!cancelled) {
          const raw = err instanceof Error ? err.message : ''
          const name = err instanceof Error ? err.name : ''
          const policyOrDenied =
            name === 'NotAllowedError' ||
            /permission|permissions policy|not allowed/i.test(raw)
          const message = policyOrDenied
            ? 'Camera is blocked for this site. Allow Camera in the browser site settings (or ask an admin to enable camera in the server Permissions-Policy), then try again — or enter the barcode manually.'
            : raw || 'Camera unavailable. Enter the barcode manually.'
          setCameraError(message)
          setUseManual(true)
        }
      } finally {
        if (!cancelled) setStarting(false)
      }
    }

    void start()
    return () => {
      cancelled = true
      stopScanner()
    }
  }, [open, useManual, onScan, stopScanner])

  if (!open) return null

  const submitManual = (e: React.FormEvent) => {
    e.preventDefault()
    const text = manual.trim()
    if (!text) return
    onScan(text)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-stretch justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex h-[100dvh] max-h-[100dvh] w-full max-w-none flex-col overflow-hidden rounded-none bg-base-100 shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:rounded-box"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between border-b border-base-200 px-4 py-3 shrink-0">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <FontAwesomeIcon icon={faBarcode} />
            {title}
          </h3>
          <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
          {!useManual ? (
            <div className="relative min-h-0 flex-1 overflow-hidden rounded-box bg-neutral sm:mx-auto sm:aspect-[3/4] sm:w-full sm:max-h-[min(28rem,55vh)] sm:flex-none">
              <video
                ref={videoRef}
                className="absolute inset-0 h-full w-full object-cover"
                muted
                playsInline
              />
              {starting ? (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral/60 text-neutral-content">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl" />
                </div>
              ) : null}
              {/* Portrait scan guide */}
              <div className="pointer-events-none absolute inset-6 rounded-box border border-primary/50" />
              <div className="pointer-events-none absolute inset-x-10 top-1/2 h-0.5 -translate-y-1/2 bg-primary/80" />
            </div>
          ) : (
            <form className="flex min-h-0 flex-1 flex-col justify-center space-y-3" onSubmit={submitManual}>
              <label className="form-control">
                <span className="label-text">Barcode value</span>
                <input
                  className="input input-bordered"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="Type or paste barcode"
                  autoFocus
                />
              </label>
              <button type="submit" className="btn btn-primary w-full" disabled={!manual.trim()}>
                Look up
              </button>
            </form>
          )}

          {cameraError ? <div className="alert alert-warning shrink-0 py-2 text-sm">{cameraError}</div> : null}

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              className={`btn flex-1 ${!useManual ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setUseManual(false)}
            >
              <FontAwesomeIcon icon={faVideo} />
              Camera
            </button>
            <button
              type="button"
              className={`btn flex-1 ${useManual ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setUseManual(true)}
            >
              <FontAwesomeIcon icon={faKeyboard} />
              Manual
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default BarcodeScanner
